import styles from "./login.module.scss";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Input } from "../../shared/components/input/input";
import { useForm } from "react-hook-form";
import {
  object,
  string
} from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useDispatch,
  useSelector
} from "react-redux";
import {
  loginUser,
  setLogin
} from "../../store/slices/authSlice";
import {
  AppDispatch,
  RootState
} from "../../store";
import {
  useCallback,
  useEffect,
  useState
} from "react";

const toolUrl = process.env.REACT_APP_TOOL_URL as string;

export const Login = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();

  const { loading, projects, userToken, login, error, isProjectAdmin, isTestToolAdmin } = useSelector((state: RootState) => state.authReducer);

  const [loginError, setLoginError] = useState<boolean>(false);

  const schema = object({
    login: string().required(t("common.notValid")),
    password: string().required(t("common.notValid")),
  })

  const {
    control,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange"
  });

  const loginWatch = watch('login');
  const password = watch('password');

  const onClickSignIn = useCallback((): void => {
    handleSubmit((value) => {
      dispatch(loginUser({ login: value.login, password: value.password }));
      dispatch(setLogin(value.login));
    })();
  }, [handleSubmit, dispatch]);

  useEffect(() => {
    const onKeyPress = (e: KeyboardEvent): void => {
      if (e.key === "Enter") {
        onClickSignIn();
      }
    }
    window.addEventListener("keydown", onKeyPress);

    return () => {
      window.removeEventListener('keydown', onKeyPress);
    }
  }, []);

  useEffect(() => {
    if (!!userToken) {
      if (projects.length === 1 && !!projects[0].token && !isProjectAdmin && !isTestToolAdmin) {
        window.location.href = `${toolUrl}#/?token=${encodeURIComponent(projects[0].token)}&projectName=${encodeURIComponent(projects[0].name)}&isSandbox=${+projects[0].isSandbox}&login=${login ? encodeURIComponent(login) : ""}`;
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [projects, userToken, isProjectAdmin, login, isTestToolAdmin]);

  useEffect(() => {
    if (error) {
      setError("login", { message: t("common.notValid") });
      setError("password", { message: t("common.notValid") });
      setLoginError(true);
    }
  }, [error]);

  useEffect(() => {
    if (loginError) {
      clearErrors("login");
      clearErrors("password");
    }
  }, [loginWatch, password]);

  return (
    <div className={styles.container}>
      <div className={styles.title}>{t("login.title")}</div>
      <div className={styles.subTitle}>{t("login.subTitle")}</div>
      <div className={styles.body}>
        <div className={styles.header}></div>
        <div className={styles.inputs}>
          <div className={styles.inputContainer}>
            <span>{t("login.login")}</span>
            <div className={styles.input}>
              <Input control={control}
                     placeholder={t("login.name")}
                     name={"login"}
                     error={!!errors.login ? errors.login.message : ""}/>
            </div>
          </div>
          <div className={styles.inputContainer}>
            <span>{t("login.password")}</span>
            <div className={styles.input}>
              <Input control={control}
                     placeholder={t("login.password")}
                     type={"password"}
                     name={"password"}
                     error={!!errors.password ? errors.password.message : ""}/>
            </div>
          </div>
        </div>
      </div>
      <button className={styles.button}
              disabled={loading}
              onClick={onClickSignIn}>
        {t("login.signIn")}
      </button>
    </div>
  )
};
